import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, BuildingIcon, BriefcaseIcon, DownloadIcon, FileSpreadsheetIcon, ContactIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { Contact } from '../../types';
import { exportContactsToExcel } from '../../utils/excelExport';
export function Contacts() {
  const {
    contacts,
    addContact,
    updateContact,
    deleteContact
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.company.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    setSelectedContact(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      position: '',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      company: contact.company,
      position: contact.position,
      notes: contact.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (contact: Contact) => {
    setSelectedContact(contact);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedContact) {
      updateContact(selectedContact.id, formData);
    } else {
      addContact(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedContact) {
      deleteContact(selectedContact.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportContactsToExcel(contacts);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-527">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-528">
        <div data-id="element-529">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-530">
            Contactos
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-531">
            Gestión de contactos B2B
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-533" />} onClick={openNewForm} data-id="element-532">
          Nuevo Contacto
        </Button>
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800" data-id="element-534">
        <div className="flex items-center justify-between" data-id="element-535">
          <div className="flex items-center gap-4" data-id="element-536">
            <div className="w-14 h-14 rounded-xl bg-rose-500 dark:bg-rose-600 flex items-center justify-center shadow-lg shadow-rose-500/20" data-id="element-537">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-538" />
            </div>
            <div data-id="element-539">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-540">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-541">
                Descarga todos los contactos en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-542">
                <div className="flex items-center gap-1.5 text-sm text-rose-700 dark:text-rose-400" data-id="element-543">
                  <ContactIcon className="w-4 h-4" data-id="element-544" />
                  <span className="font-medium" data-id="element-545">
                    {contacts.length} contactos
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-546">
                  • Incluye: Nombre, Empresa, Cargo, Contacto, Notas
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-548" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-rose-500/20 hover:shadow-xl hover:shadow-rose-500/30 transition-all duration-200" data-id="element-547">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Search */}
      <Card data-id="element-549">
        <div className="relative" data-id="element-550">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-551" />
          <Input placeholder="Buscar contactos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-552" />
        </div>
      </Card>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-553">
        {filteredContacts.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-554">
            No se encontraron contactos
          </div> : filteredContacts.map(contact => <Card key={contact.id} data-id="element-555">
              <div className="flex items-start justify-between mb-4" data-id="element-556">
                <div className="flex items-center gap-3" data-id="element-557">
                  <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400 font-bold" data-id="element-558">
                    {contact.name.charAt(0)}
                  </div>
                  <div data-id="element-559">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-560">
                      {contact.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-561">
                      {contact.position}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1" data-id="element-562">
                  <button onClick={() => openEditForm(contact)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-563">
                    <EditIcon className="w-4 h-4" data-id="element-564" />
                  </button>
                  <button onClick={() => openDeleteConfirm(contact)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-565">
                    <TrashIcon className="w-4 h-4" data-id="element-566" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-4" data-id="element-567">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-568">
                  <BuildingIcon className="w-4 h-4" data-id="element-569" />
                  <span data-id="element-570">{contact.company}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-571">
                  <MailIcon className="w-4 h-4" data-id="element-572" />
                  <span data-id="element-573">{contact.email}</span>
                </div>
                {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-574">
                    <PhoneIcon className="w-4 h-4" data-id="element-575" />
                    <span data-id="element-576">{contact.phone}</span>
                  </div>}
              </div>

              {contact.notes && <p className="text-sm text-gray-500 dark:text-gray-400 italic border-t border-gray-100 dark:border-gray-700 pt-3" data-id="element-577">
                  "{contact.notes}"
                </p>}
            </Card>)}
      </div>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'} size="md" data-id="element-578">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-579">
          <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} data-id="element-580" />

          <div className="grid grid-cols-2 gap-4" data-id="element-581">
            <Input label="Empresa" value={formData.company} onChange={e => setFormData({
            ...formData,
            company: e.target.value
          })} data-id="element-582" />
            <Input label="Cargo / Posición" value={formData.position} onChange={e => setFormData({
            ...formData,
            position: e.target.value
          })} data-id="element-583" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-584">
            <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} data-id="element-585" />
            <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} error={formErrors.email} data-id="element-586" />
          </div>

          <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={3} data-id="element-587" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-588">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-589">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-590">
              {selectedContact ? 'Guardar Cambios' : 'Crear Contacto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-591">
        <div className="space-y-4" data-id="element-592">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-593">
            ¿Está seguro de que desea eliminar a "{selectedContact?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-594">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-595">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-596">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}